/**
 * HowToDetailPage.jsx — /howto/:id — one entry with player + notes.
 *
 * Uses the list query (cache-warm from the grid); "More in {category}"
 * strip keeps the page from being a dead end.
 */
import { useState, useMemo, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Typography, Button, IconButton, Chip, Stack, Paper, Divider,
  CircularProgress, Snackbar, Alert,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  OpenInNew as OpenIcon,
  Link as LinkIcon,
  School as SchoolIcon,
  AutoAwesome as InspirationIcon,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import {
  useWikiArticles, useUpdateWikiArticle, useDeleteWikiArticle,
} from '../hooks/useWiki';
import { useRBAC } from '../hooks/useRBAC';
import { useSettings } from '../hooks/useSettings';
import { useAuth } from '../hooks/useAuth';
import { useMobile } from '../hooks/useMobile';
import { parseVideoUrl, urlDomain, isTrustedThumbnailHost } from '../lib/videoUrl';
import HowToEditDialog from '../components/howto/HowToEditDialog';
import FocusHide from '../components/shared/FocusHide';

const MD_SX = {
  overflowWrap: 'anywhere',
  '& h1, & h2, & h3': { fontWeight: 700, mt: 2, mb: 1 },
  '& h1': { fontSize: '1.4rem' },
  '& h2': { fontSize: '1.2rem' },
  '& h3': { fontSize: '1.05rem' },
  '& p': { lineHeight: 1.65, my: 1 },
  '& ul, & ol': { pl: 3 },
  '& li': { mb: 0.5 },
  '& code': {
    fontFamily: 'ui-monospace, SFMono-Regular, monospace',
    fontSize: '0.85em',
    bgcolor: 'action.hover',
    px: 0.5, py: 0.2, borderRadius: 0.5,
  },
  '& pre': { bgcolor: 'action.hover', p: 1.5, borderRadius: 1, overflow: 'auto', '& code': { bgcolor: 'transparent', p: 0 } },
  '& a': { color: 'primary.main' },
  '& img': { maxWidth: '100%', height: 'auto', borderRadius: 1 },
  '& blockquote': { borderLeft: 3, borderColor: 'divider', pl: 2, ml: 0, color: 'text.secondary' },
};

export default function HowToDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useMobile();
  const { hasAccess } = useRBAC();
  const { data: settings } = useSettings();
  const { profile } = useAuth();
  const canEdit = hasAccess('wiki', 'edit', settings);
  const canDelete = hasAccess('wiki', 'full', settings);

  const { data: entries = [], isLoading, isError } = useWikiArticles();
  const updateMut = useUpdateWikiArticle();
  const deleteMut = useDeleteWikiArticle();

  const entry = useMemo(() => entries.find((e) => e.id === id), [entries, id]);
  const video = entry ? parseVideoUrl(entry.url) : null;

  const related = useMemo(() => {
    if (!entry) return [];
    return entries
      .filter((e) => e.id !== entry.id && (e.category || 'General') === (entry.category || 'General'))
      .slice(0, 6);
  }, [entries, entry]);

  // Frozen snapshot passed to the edit dialog — using the live `entry` would
  // reset the open dialog's fields whenever the list refetches (websocket).
  const [editEntry, setEditEntry] = useState(null);
  const [snack, setSnack] = useState(null);

  // Landing on a new entry (e.g. via "More in {category}") should start at the
  // top, not wherever the previous page was scrolled to.
  const topRef = useRef(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { topRef.current?.scrollIntoView({ block: 'start' }); }, [id]);

  const goBack = () => {
    // Only step back in history if we arrived from within the app; a direct/
    // shared link has no in-app history and would otherwise exit the portal.
    if (location.state?.canGoBack) navigate(-1);
    else navigate('/howto');
  };

  const handleSave = async (payload) => {
    try {
      await updateMut.mutateAsync({ ...payload, updatedBy: profile?.id || null });
      setSnack({ kind: 'success', msg: 'Entry updated' });
      setEditEntry(null);
    } catch (err) {
      setSnack({ kind: 'error', msg: err.message || 'Save failed' });
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this entry? This cannot be undone.')) return;
    try {
      await deleteMut.mutateAsync(entry.id);
      navigate('/howto', { replace: true });
    } catch (err) {
      setSnack({ kind: 'error', msg: err.message || 'Delete failed' });
    }
  };

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={28} /></Box>;
  }

  if (!entry) {
    // Distinguish a real "gone" from a transient fetch failure — telling a user
    // their entry was deleted when the network just hiccuped is alarming.
    const failed = isError;
    return (
      <Box sx={{ maxWidth: 640, mx: 'auto', textAlign: 'center', py: 8 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>{failed ? 'Couldn’t load this entry' : 'Entry not found'}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {failed ? 'Check your connection and try again.' : 'It may have been deleted.'}
        </Typography>
        <Stack direction="row" spacing={1} justifyContent="center">
          {failed && (
            <Button variant="contained" size="small" onClick={() => window.location.reload()} sx={{ textTransform: 'none' }}>
              Retry
            </Button>
          )}
          <Button variant="outlined" size="small" startIcon={<BackIcon />} onClick={() => navigate('/howto')} sx={{ textTransform: 'none' }}>
            Back to How To
          </Button>
        </Stack>
      </Box>
    );
  }

  const KindIcon = entry.kind === 'inspiration' ? InspirationIcon : SchoolIcon;
  const createdDate = entry.createdAt ? new Date(entry.createdAt).toLocaleDateString() : null;

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      {/* Top bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton onClick={goBack} size="small"><BackIcon /></IconButton>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          How To / {entry.kind === 'inspiration' ? 'Inspiration' : 'How-To'}
        </Typography>
        {canEdit && (
          <Button size="small" startIcon={<EditIcon />} onClick={() => setEditEntry(entry)} sx={{ textTransform: 'none' }}>
            Edit
          </Button>
        )}
      </Box>

      <Box ref={topRef} />

      {/* Player / link hero */}
      {video ? (
        <Box
          sx={video.provider === 'instagram'
            ? { position: 'relative', maxWidth: 420, mx: 'auto', height: 600, bgcolor: 'black', borderRadius: 2, overflow: 'hidden', mb: 2 }
            : { position: 'relative', aspectRatio: '16 / 9', bgcolor: 'black', borderRadius: 2, overflow: 'hidden', mb: 2 }}
        >
          <iframe
            src={video.embedUrl}
            title={entry.title}
            allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
            allowFullScreen
            scrolling="no"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
          />
        </Box>
      ) : entry.url ? (
        <Paper variant="outlined" sx={{ p: 3, mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <LinkIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" color="text.secondary" noWrap>{urlDomain(entry.url)}</Typography>
          </Box>
          <Button
            variant="contained"
            size="small"
            endIcon={<OpenIcon />}
            href={entry.url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ textTransform: 'none', flexShrink: 0 }}
          >
            Open Link
          </Button>
        </Paper>
      ) : null}

      {/* Title + meta */}
      <Box sx={{ mb: 2 }}>
        <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 700, lineHeight: 1.25, mb: 1 }}>
          {entry.title}
        </Typography>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          <Chip icon={<KindIcon sx={{ fontSize: 14 }} />} label={entry.kind === 'inspiration' ? 'Inspiration' : 'How-To'} size="small" variant="outlined" color="primary" />
          {entry.category && <Chip label={entry.category} size="small" variant="outlined" />}
          {video && (
            <Button
              size="small"
              endIcon={<OpenIcon sx={{ fontSize: 14 }} />}
              href={video.watchUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ textTransform: 'none', color: 'text.secondary' }}
            >
              Open on {video.provider === 'vimeo' ? 'Vimeo' : video.provider === 'instagram' ? 'Instagram' : 'YouTube'}
            </Button>
          )}
          {createdDate && (
            <Typography variant="caption" color="text.disabled">Added {createdDate}</Typography>
          )}
        </Stack>
        {entry.description && (
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1.5 }}>
            {entry.description}
          </Typography>
        )}
      </Box>

      {/* Notes */}
      {entry.bodyMd && (
        <Paper variant="outlined" sx={{ p: isMobile ? 2 : 3, mb: 3, ...MD_SX, '& > *:first-of-type': { mt: 0 } }}>
          <ReactMarkdown>{entry.bodyMd}</ReactMarkdown>
        </Paper>
      )}

      {/* More in category */}
      <FocusHide k="howto.related">
        {related.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              More in {entry.category || 'General'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 1 }}>
              {related.map((r) => {
                const rv = parseVideoUrl(r.url);
                const rThumb = (isTrustedThumbnailHost(r.thumbnailUrl) ? r.thumbnailUrl : null) || rv?.thumbnailUrl;
                return (
                  <Box
                    key={r.id}
                    data-track="howto.relatedCard"
                    onClick={() => navigate(`/howto/${r.id}`, { state: { canGoBack: true } })}
                    sx={{ width: 180, flexShrink: 0, cursor: 'pointer', '&:hover .relTitle': { color: 'primary.main' } }}
                  >
                    <Box sx={{ aspectRatio: '16 / 9', borderRadius: 1.5, overflow: 'hidden', bgcolor: 'action.hover', mb: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {rThumb ? (
                        <Box component="img" src={rThumb} alt={r.title} loading="lazy" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <LinkIcon sx={{ color: 'text.disabled' }} />
                      )}
                    </Box>
                    <Typography className="relTitle" variant="caption" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.3 }}>
                      {r.title}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </FocusHide>

      {/* Edit dialog — uses a frozen snapshot so a background refetch can't
          wipe in-progress edits */}
      <HowToEditDialog
        open={!!editEntry}
        initial={editEntry}
        existingCategories={Array.from(new Set(entries.map((e) => e.category).filter(Boolean)))}
        onClose={() => setEditEntry(null)}
        onSave={handleSave}
        onDelete={canDelete ? handleDelete : undefined}
        canDelete={canDelete}
        saving={updateMut.isPending || deleteMut.isPending}
      />

      <Snackbar
        open={!!snack}
        autoHideDuration={3000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snack ? (
          <Alert severity={snack.kind} onClose={() => setSnack(null)} variant="filled">{snack.msg}</Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
